import React, { useEffect, useState } from 'react';
import { Control, FieldErrorsImpl, RegisterOptions, useController } from 'react-hook-form';
import { AgentFormValues } from '../../interfaces/types';
import useUpload, { UPLOAD_STATUS } from '@cp/components/UploadComponent/useUpload';
import UploadComponent from '@cp/components/UploadComponent/UploadComponent';
import UploadErrorComponent from '@cp/components/UploadComponent/UploadErrorComponent';
import UploadIcon from '@navi/web-ui/lib/icons/UploadIcon';
import UploadSuccessComponent from '@cp/components/UploadComponent/UploadSuccessComponent';
import axiosInstance, { ApiKeys, getApiUrl } from '@cp/utils/ApiHelper';
import UploadLoadingComponent from '@cp/src/components/UploadComponent/UploadLoadingComponent';

type AgentFormUploadControlProps = {
  control: Control<AgentFormValues, any>;
  name: keyof AgentFormValues;
  rules: RegisterOptions;
  errors: Partial<FieldErrorsImpl<AgentFormValues>>;
  isEditDisabled: boolean | undefined;
  styles: CSSModuleClasses;
  inputLabel?: string;
  isRequired?: boolean;
  setSubmitButtonDisabled: (isDisabled: boolean) => void;
  documentType?: string | number;
  documentContentType?: string;
  supportedFile?: string;
};

const customAgentFormUploadComponent = (handleUploadClick: (e: React.ChangeEvent<any>) => void) => {
  return (
    <div>
      <div className="flex items-center gap-2">
        <UploadIcon color="var(--blue-base)" width={20} height={20} />
        <div
          className="text-[--blue-base] font-medium cursor-pointer"
          onClick={e => {
            handleUploadClick(e);
          }}
        >
          {' '}
          Click to Upload{' '}
          <span className="text-[--grayscale-content-2]">or drag and drop here</span>
        </div>
      </div>
    </div>
  );
};
const AgentFormUploadControl: React.FC<AgentFormUploadControlProps> = props => {
  const {
    control,
    name,
    rules,
    errors,
    styles,
    isEditDisabled = false,
    inputLabel = 'Upload DRA Certificate',
    isRequired = false,
    setSubmitButtonDisabled,
    documentType = 'DRA_CERTIFICATE',
    documentContentType = 'PDF',
    supportedFile = '.pdf'
  } = props;

  const { field } = useController({
    name,
    control,
    rules
  });

  const { file, setFile, upload, fileStatus, error, setError } = useUpload({});
  const [showLoadingComponent, setShowLoadingComponent] = useState(false);
  useEffect(() => {
    if (file) {
      setShowLoadingComponent(true);
      const getUploadUrl = getApiUrl(
        ApiKeys.GET_DOCUMENT_UPLOAD_URL,
        {},
        {
          documentType: documentType,
          documentContentType: documentContentType
        }
      );
      axiosInstance
        .get(getUploadUrl)
        .then(async res => {
          //presigned url fetch and pass it to upload function
          setSubmitButtonDisabled(true);
          upload(file, res.data?.preSignedPutUrl)
            ?.then(() => {
              //on success
              const ackUrl = getApiUrl(ApiKeys.DOCUMENT_UPLOAD_ACK_URL);

              axiosInstance
                .post(ackUrl, {
                  referenceId: res?.data?.referenceId,
                  status: 'UPLOAD_SUCCESS',
                  message: ''
                })
                .then(res => {
                  field.onChange(res?.data);
                });
            })
            .catch(err => {
              setError({
                message: 'Error in uploading document' || '',
                fileName: file?.name || ''
              });
            })
            .finally(() => {
              setSubmitButtonDisabled(false);
            });
        })
        .catch(err => {
          setError({ message: 'Error in uploading document' || '', fileName: file?.name || '' });
        })
        .finally(() => {
          setShowLoadingComponent(false);
        });
    }
  }, [file]);

  if (isEditDisabled) return <></>;
  if (showLoadingComponent) {
    return (
      <>
        <div className="text-[--grayscale-2] text-[14px] ml-[20px] mt-[16px]">
          {inputLabel}
          {isRequired && <span className="text-red-500 ml-1">*</span>}
        </div>
        <UploadLoadingComponent
          containerClassName="w-[352px] box-border ml-[18px] !px-[16px] !py-[24px] border border-solid !border-[var(--blue-border)] !bg-[var(--bg-blue)]"
          name={file?.name || ''}
          isInProgress={true}
          onCancel={() => {
            setFile(null);
          }}
        />
      </>
    );
  }

  return (
    <>
      <div className="text-[--grayscale-2] text-[14px] ml-[20px] mt-[16px]">
        {inputLabel}
        {isRequired && <span className="text-red-500 ml-1">*</span>}
      </div>
      {error?.message ? (
        <div className={styles.errorContainer}>
          <UploadErrorComponent
            containerClassName="w-[352px] box-border ml-[18px] !px-[16px] !py-[24px]"
            message={error?.message || ''}
            fileName={error?.fileName || ''}
            onCancel={() => {
              setError({ message: '', fileName: '' });
              setFile(null);
              field.onChange(null);
            }}
          />
        </div>
      ) : null}
      {error?.message ? null : (
        <div>
          {file ? (
            <UploadSuccessComponent
              containerClassName="w-[348px] box-border ml-[18px] !px-[16px] !py-[24px]"
              name={file?.name || ''}
              isInProgress={fileStatus === UPLOAD_STATUS.IN_PROGRESS}
              onCancel={() => {
                setFile(null);
                field.onChange(null);
              }}
            />
          ) : (
            <div className="w-[360px] ml-5 mt-3">
              <UploadComponent
                setFile={setFile}
                file={file}
                uploadContentContainer={customAgentFormUploadComponent}
                containerClassName="w-[348px] !py-[16px] !px-[0] box-border !m-0"
                supportedFile={supportedFile}
                setHasError={setError}
              />
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default AgentFormUploadControl;
